/* ============================================================================
   CourseVideos — upload, organização em capítulos e ordenação (drag & drop).
   ============================================================================ */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { API_CONFIG } from '../../config/api';
import AdminLayout from '../components/AdminLayout';
import { Modal, confirm } from '../components/Modal';
import { toast } from '../components/Toast';
import { EmptyState } from '../components/ui';
import {
  IconBack, IconUpload, IconFolder, IconFilm, IconChapter, IconGrip,
  IconEdit, IconTrash, IconX, IconCheck, IconSave, IconPlus,
} from '../components/icons';
import './CourseVideos.css';

const fileName = (key) => key.split('/').pop();
const slug = (name) => name.toLowerCase().replace(/ /g, '-');

export default function CourseVideos() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const fileRef = useRef(null);

  const [course, setCourse] = useState(null);
  const [videos, setVideos] = useState([]);       // vídeos sem capítulo
  const [chapters, setChapters] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [dragOver, setDragOver] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedVideo, setDraggedVideo] = useState(null);

  const [showChapterModal, setShowChapterModal] = useState(false);
  const [newChapter, setNewChapter] = useState('');
  const [editing, setEditing] = useState(null); // { type, id, value }

  useEffect(() => { loadCourse(); }, [courseId]); // eslint-disable-line

  const loadCourse = async () => {
    try {
      setLoadingPage(true);
      const r = await api.get('/admin/courses', true);
      if (r.success) {
        const found = (r.data || []).find((c) => (c.id || c.PK?.replace('COURSE#', '')) === courseId);
        if (found) { setCourse(found); await loadVideos(found.folderName); }
      }
    } catch (e) { console.error(e); }
    finally { setLoadingPage(false); }
  };

  const loadVideos = async (folderName) => {
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/videos/list?folderName=${folderName}`);
      if (!res.ok) throw new Error();
      const data = (await res.json()).data || {};
      const chaptersData = Array.isArray(data.chapters) ? data.chapters : [];
      setChapters(chaptersData);
      const inChapters = chaptersData.flatMap((ch) => ch.videos.map((v) => fileName(v.key)));
      setVideos((data.videos || [])
        .filter((k) => !inChapters.includes(fileName(k)))
        .map((k, i) => ({ key: k, title: k.replace('.mp4', '').replace(/-/g, ' '), order: i + 1 })));
    } catch { setVideos([]); setChapters([]); }
  };

  /* ── Upload ── */
  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true); setProgress(0);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const num = videos.length + i + 1;
        const name = `video${num}.${file.name.split('.').pop()}`;
        const ur = await api.post('/admin/video-upload', { folderName: course.folderName, fileName: name, contentType: file.type || 'video/mp4' }, true);
        if (!ur?.uploadUrl) throw new Error('Erro ao gerar URL de upload.');
        const put = await fetch(ur.uploadUrl, { method: 'PUT', body: file });
        if (!put.ok) throw new Error(`Falha no upload: ${name}`);
        setVideos((prev) => [...prev, { key: name, title: file.name.replace(/\.[^/.]+$/, ''), order: num }]);
        setProgress(Math.round(((i + 1) / files.length) * 100));
      }
      toast.success(`${files.length} vídeo${files.length > 1 ? 's' : ''} enviado${files.length > 1 ? 's' : ''}.`);
    } catch (e) {
      toast.error(e.message || 'Erro no upload.');
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const onDropUpload = (e) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('video/'));
    if (files.length) uploadFiles(files);
  };

  /* ── Reordenação de vídeos soltos ── */
  const reorder = (dropIndex) => {
    if (draggedIndex === null) return;
    const arr = [...videos];
    const [moved] = arr.splice(draggedIndex, 1);
    arr.splice(dropIndex, 0, moved);
    arr.forEach((v, i) => { v.order = i + 1; });
    setVideos(arr); setDraggedIndex(null);
  };

  /* ── Capítulos: drop de vídeo solto dentro ── */
  const dropInChapter = (chapterId) => {
    if (!draggedVideo || draggedVideo.chapterId) return;
    const vid = draggedVideo.video || draggedVideo;
    const ch = chapters.find((c) => c.id === chapterId);
    const updated = { ...vid, key: `${slug(ch.name)}/${fileName(vid.key)}` };
    setVideos((prev) => prev.filter((v) => v.key !== vid.key));
    setChapters((prev) => prev.map((c) => c.id === chapterId ? { ...c, videos: [...c.videos, updated] } : c));
    setDraggedVideo(null);
  };

  const dragVideoInChapter = (chapterId, idx) => {
    const ch = chapters.find((c) => c.id === chapterId);
    setDraggedVideo({ chapterId, videoIndex: idx, video: ch.videos[idx] });
  };

  const dropVideoInChapter = (chapterId, dropIndex) => {
    if (!draggedVideo?.chapterId || draggedVideo.chapterId !== chapterId) return;
    if (draggedVideo.videoIndex === dropIndex) { setDraggedVideo(null); return; }
    setChapters((prev) => prev.map((ch) => {
      if (ch.id !== chapterId) return ch;
      const arr = [...ch.videos];
      const [m] = arr.splice(draggedVideo.videoIndex, 1);
      arr.splice(dropIndex, 0, m);
      return { ...ch, videos: arr };
    }));
    setDraggedVideo(null);
  };

  const removeFromChapter = (chapterId, key) => {
    const ch = chapters.find((c) => c.id === chapterId);
    const removed = ch.videos.find((v) => v.key === key);
    if (!removed) return;
    setChapters((prev) => prev.map((c) => c.id === chapterId ? { ...c, videos: c.videos.filter((v) => v.key !== key) } : c));
    if (!videos.some((v) => fileName(v.key) === fileName(key))) {
      setVideos((prev) => [...prev, { ...removed, key: fileName(key) }]);
    }
  };

  /* ── Criar capítulo ── */
  const createChapter = () => {
    if (!newChapter.trim()) return;
    setChapters((prev) => [...prev, { id: Date.now().toString(), name: newChapter.trim(), videos: [] }]);
    setNewChapter(''); setShowChapterModal(false);
  };

  /* ── Edição inline de títulos ── */
  const saveEdit = () => {
    if (!editing || !editing.value.trim()) { setEditing(null); return; }
    const { type, id, value } = editing;
    const v = value.trim();
    if (type === 'video') setVideos((p) => p.map((x) => x.key === id ? { ...x, title: v } : x));
    else if (type === 'chapter') setChapters((p) => p.map((c) => c.id === id ? { ...c, name: v } : c));
    else if (type === 'chapter-video') {
      const [chId, vKey] = id.split('::');
      setChapters((p) => p.map((c) => c.id === chId ? { ...c, videos: c.videos.map((x) => x.key === vKey ? { ...x, title: v } : x) } : c));
    }
    setEditing(null);
  };

  const editProps = () => ({
    className: 'cv-inline-input', autoFocus: true, value: editing.value,
    onChange: (e) => setEditing((t) => ({ ...t, value: e.target.value })),
    onBlur: saveEdit,
    onKeyDown: (e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); },
  });

  /* ── Deletar ── */
  const deleteVideo = async (video) => {
    const ok = await confirm({ title: 'Remover vídeo', message: `Remover "${video.title}" do S3? Esta ação não pode ser desfeita.`, confirmLabel: 'Remover vídeo', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/admin/delete-video?folderName=${course.folderName}&videoKey=${video.key}`, true);
      setVideos((prev) => { const arr = prev.filter((v) => v.key !== video.key); arr.forEach((v, i) => { v.order = i + 1; }); return arr; });
      toast.success('Vídeo removido.');
    } catch (e) { toast.error(e.message || 'Erro ao remover.'); }
  };

  const deleteChapter = async (ch) => {
    const ok = await confirm({ title: 'Remover capítulo', message: `Remover o capítulo "${ch.name}"? Os vídeos voltam para a lista principal.`, confirmLabel: 'Remover capítulo', danger: true });
    if (!ok) return;
    setVideos((prev) => [...prev, ...ch.videos.map((v) => ({ ...v, key: fileName(v.key) }))]);
    setChapters((prev) => prev.filter((c) => c.id !== ch.id));
    toast.success(`Capítulo "${ch.name}" removido.`);
  };

  /* ── Salvar tudo ── */
  const saveAll = async () => {
    try {
      setSaving(true);
      for (const ch of chapters) {
        for (const v of ch.videos) {
          if (!v.key) continue;
          const expected = `${slug(ch.name)}/${fileName(v.key)}`;
          if (v.key !== expected) {
            await api.post('/admin/move-video', { folderName: course.folderName, videoKey: v.key, chapterName: ch.name }, true);
            v.key = expected;
          }
        }
      }
      const res = await api.post('/admin/save-videos-json', {
        folderName: course.folderName,
        videosJson: {
          videos: videos.map((v) => ({ key: v.key, title: v.title, order: v.order })),
          chapters: chapters.map((ch) => ({ id: ch.id, name: ch.name, videos: ch.videos.map((v) => ({ key: v.key, title: v.title })) })),
        },
      }, true);
      if (!res.success) throw new Error('Erro ao salvar.');
      setSaved(true);
      toast.success('Ordem e capítulos salvos.');
      setTimeout(() => setSaved(false), 2200);
    } catch (e) { toast.error(e.message || 'Erro ao salvar.'); }
    finally { setSaving(false); }
  };

  const inChapterCount = chapters.reduce((s, c) => s + c.videos.length, 0);
  const totalVideos = videos.length + inChapterCount;

  if (loadingPage) return <AdminLayout><div className="mg-loading"><div className="mg-spinner" /></div></AdminLayout>;
  if (!course) return (
    <AdminLayout>
      <EmptyState icon={<IconFilm size={26} />} title="Curso não encontrado"
        action={<button className="mg-btn mg-btn-primary" onClick={() => navigate('/treinamentos/manager/courses')}>Voltar aos cursos</button>} />
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <button className="cf-breadcrumb" onClick={() => navigate('/treinamentos/manager/courses')}><IconBack size={15} /> Cursos</button>
      <h1 className="mg-page-title">Vídeos do curso</h1>
      <p className="mg-page-sub">{course.title}</p>

      <div className="cv-chips">
        <span className="cv-chip"><IconFilm size={15} /> {totalVideos} vídeos</span>
        <span className="cv-chip"><IconChapter size={15} /> {chapters.length} capítulos</span>
        <span className="cv-chip cv-chip-folder"><IconFolder size={15} /> {course.folderName}</span>
      </div>

      <div className="cv-layout">
        {/* ── Coluna esquerda: upload + ações ── */}
        <aside className="cv-side">
          <div
            className={`cv-dropzone${dragOver ? ' is-over' : ''}${uploading ? ' is-busy' : ''}`}
            onClick={() => !uploading && fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); !uploading && setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDropUpload}
          >
            {uploading ? (
              <>
                <span className="cv-upload-pct">{progress}%</span>
                <p>Enviando vídeos…</p>
                <div className="cv-progress"><div style={{ width: `${progress}%` }} /></div>
              </>
            ) : (
              <>
                <div className="cv-dropzone-icon"><IconUpload size={28} /></div>
                <p className="cv-dropzone-title">{dragOver ? 'Solte para enviar!' : 'Arraste ou clique para enviar'}</p>
                <p className="cv-dropzone-hint">MP4, MOV, WebM · vários de uma vez</p>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept="video/*" multiple style={{ display: 'none' }} onChange={(e) => { uploadFiles(Array.from(e.target.files)); e.target.value = ''; }} />

          <div className="cv-side-stats">
            <div><span className="cv-side-stat-val">{videos.length}</span><span className="cv-side-stat-lbl">Sem capítulo</span></div>
            <div className="cv-side-stat-sep" />
            <div><span className="cv-side-stat-val">{inChapterCount}</span><span className="cv-side-stat-lbl">Em capítulos</span></div>
          </div>

          <button className="mg-btn mg-btn-ghost mg-btn-block" onClick={() => setShowChapterModal(true)}><IconChapter size={16} /> Criar capítulo</button>
          {(videos.length > 0 || chapters.length > 0) && (
            <button className="mg-btn mg-btn-primary mg-btn-block" onClick={saveAll} disabled={saving || saved}>
              {saved ? <><IconCheck /> Salvo!</> : saving ? <><span className="mg-spinner mg-spinner-sm" /> Salvando…</> : <><IconSave size={16} /> Salvar tudo</>}
            </button>
          )}
          <p className="cv-hint">Arraste vídeos para reordenar ou soltá-los em um capítulo.</p>
        </aside>

        {/* ── Coluna direita: capítulos + vídeos ── */}
        <div className="cv-main">
          {chapters.length > 0 && (
            <section className="cv-section">
              <h2 className="cv-section-title">Capítulos</h2>
              <div className="cv-chapters">
                {chapters.map((ch) => (
                  <div key={ch.id} className="cv-chapter" onDragOver={(e) => e.preventDefault()} onDrop={() => dropInChapter(ch.id)}>
                    <div className="cv-chapter-head">
                      <div className="cv-chapter-name-wrap">
                        <span className="cv-chapter-icon"><IconChapter size={16} /></span>
                        {editing?.type === 'chapter' && editing.id === ch.id
                          ? <input {...editProps()} />
                          : <span className="cv-chapter-name">{ch.name}</span>}
                        <span className="cv-count">{ch.videos.length}</span>
                      </div>
                      <div className="cv-row-btns">
                        <button className="mg-icon-btn" onClick={() => setEditing({ type: 'chapter', id: ch.id, value: ch.name })} title="Renomear"><IconEdit size={14} /></button>
                        <button className="mg-icon-btn is-danger" onClick={() => deleteChapter(ch)} title="Remover capítulo"><IconTrash size={14} /></button>
                      </div>
                    </div>
                    <div className="cv-chapter-body">
                      {ch.videos.length === 0 ? (
                        <div className="cv-chapter-empty">Arraste vídeos para cá</div>
                      ) : ch.videos.map((v, idx) => (
                        <div key={v.key} className="cv-video" draggable
                          onDragStart={() => dragVideoInChapter(ch.id, idx)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => dropVideoInChapter(ch.id, idx)}>
                          <span className="cv-grip"><IconGrip size={16} /></span>
                          <span className="cv-video-num">{idx + 1}</span>
                          <span className="cv-video-icon"><IconFilm size={16} /></span>
                          <div className="cv-video-meta">
                            {editing?.type === 'chapter-video' && editing.id === `${ch.id}::${v.key}`
                              ? <input {...editProps()} />
                              : <span className="cv-video-title">{v.title}</span>}
                            <span className="cv-video-key">{fileName(v.key)}</span>
                          </div>
                          <div className="cv-row-btns">
                            <button className="mg-icon-btn" onClick={() => setEditing({ type: 'chapter-video', id: `${ch.id}::${v.key}`, value: v.title })} title="Editar título"><IconEdit size={14} /></button>
                            <button className="mg-icon-btn is-danger" onClick={() => removeFromChapter(ch.id, v.key)} title="Retirar do capítulo"><IconX size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="cv-section">
            <h2 className="cv-section-title">{chapters.length > 0 ? 'Sem capítulo' : 'Vídeos'}</h2>
            {videos.length === 0 ? (
              <EmptyState icon={<IconFilm size={26} />} title={chapters.length > 0 ? 'Todos os vídeos estão em capítulos' : 'Nenhum vídeo ainda'}>
                {chapters.length > 0 ? 'Excelente organização!' : 'Envie vídeos usando a área ao lado.'}
              </EmptyState>
            ) : (
              <div className="cv-videos">
                {videos.map((video, i) => (
                  <div key={video.key} className={`cv-video${draggedIndex === i ? ' is-dragging' : ''}`} draggable
                    onDragStart={() => { setDraggedIndex(i); setDraggedVideo(video); }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => reorder(i)}
                    onDragEnd={() => { setDraggedIndex(null); setDraggedVideo(null); }}>
                    <span className="cv-grip"><IconGrip size={16} /></span>
                    <span className="cv-video-num">{video.order}</span>
                    <span className="cv-video-icon"><IconFilm size={16} /></span>
                    <div className="cv-video-meta">
                      {editing?.type === 'video' && editing.id === video.key
                        ? <input {...editProps()} />
                        : <span className="cv-video-title">{video.title}</span>}
                      <span className="cv-video-key">{video.key}</span>
                    </div>
                    <div className="cv-row-btns">
                      <button className="mg-icon-btn" onClick={() => setEditing({ type: 'video', id: video.key, value: video.title })} title="Editar título"><IconEdit size={14} /></button>
                      <button className="mg-icon-btn is-danger" onClick={() => deleteVideo(video)} title="Remover vídeo"><IconTrash size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {showChapterModal && (
        <Modal
          title="Novo capítulo"
          subtitle="Crie uma seção para organizar os vídeos do curso."
          onClose={() => setShowChapterModal(false)}
          actions={<>
            <button className="mg-btn mg-btn-ghost" onClick={() => setShowChapterModal(false)}>Cancelar</button>
            <button className="mg-btn mg-btn-primary" onClick={createChapter} disabled={!newChapter.trim()}><IconPlus /> Criar</button>
          </>}
        >
          <input className="mg-input" autoFocus value={newChapter}
            onChange={(e) => setNewChapter(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createChapter(); }}
            placeholder="Ex: Módulo 01 — Fundamentos" />
        </Modal>
      )}
    </AdminLayout>
  );
}
